/**
 * Apps and routines (D354, D363) and workflows (D401): what people share from their workbench for a project, through the
 * organisation's catalogue (`/apps/v1`, `/routines/v1`, `/workflows/v1`). A page of its own under Rules beside Customisations rather than a part of the
 * Catalogue: these are published by people for one project, versioned and immutable, counted as people add them, and
 * reviewed by the project's owners, where the Catalogue is what administrators publish for every workbench.
 *
 * For each kind: the figures (listed, waiting for review, how often added), the review queue (Approve, or Reject with the
 * reason the author reads), and the catalogue by project, each with its newest version's state, its versions and Withdraw.
 * What publishing does is the policy's (`microapps.publishing`, `routines.publishing`), set under Policies.
 *
 * Apps governance (D364): each app says what it reads, its sources outside the Workbench and what it sends out (its statement,
 * the approved one when an owner approved it), with its label when above Internal and "External data" when it has outside
 * sources. Approve is the organisation's consent to the statement shown; Revoke withdraws it and lists the app under the
 * policy's `microapps.revoked`, so workbenches withdraw it on their next pull. Each app's history is the workbenches' own audit
 * of consents, refusals and data sent out.
 *
 * Workflows (D401) are a third catalogue here rather than a page of their own: they are published, reviewed, versioned and
 * withdrawn exactly as apps and routines are, by the same people for the same project, and a routine or a forwarded event
 * (When something happens, beside them) is what starts most of them. Their tab adds what only a workflow has: its runs, the
 * ones waiting for a person first, each opening the run's page; a workflow opens its own page (pages/WorkflowPages.tsx)
 * with its picture, its versions and Run now.
 */

import { deriveStatement, externalNames, labelRank, labelWord, ROUTINE_LISTING_STATUS_WORDS, ROUTINE_RUN_WORDS, ROUTINE_SCHEDULE_WORDS, statementLines, type AppStatement } from '@aioe/shared';
import { BranchesRight, CircleQuestion, Clock, LayoutCellsLarge, Persons } from '@gravity-ui/icons';
import { Segment } from '@heroui-pro/react';
import { useState, type ReactNode } from 'react';
import { auditSentence } from '../components/events.ts';

import { problemText, sharedCatalogueRoutes, type OrganisationApp, type OrganisationRoutine, type PublishingRule } from '../api/client.ts';
import type { OrganisationWorkflows } from '@aioe/shared';
import { Button, ConfirmDialog, Empty, ErrorNote, inputClass, ListBlock, ListGroup, ListRow, PageColumn, PageHeader, Stat, StatusMark } from '../components/ui.tsx';
import { Explainer } from '../components/ui/Layout.tsx';
import { RowMenu } from '../components/ui/RowMenu.tsx';
import { VIEW_TONES } from '../components/ui/Art.tsx';
import { PartBoundary } from '../components/ui/PartBoundary.tsx';
import { WhenSomethingHappens } from '../components/routineEvents.tsx';
import { timeAgo, useLoad } from '../hooks.ts';
import { listCount, NoMatch, ShowMore, useListSearch } from '../pagedList.tsx';
import { SearchBox } from '../components/ui/SearchBox.tsx';
import { holds, scopesHolding, usePermissions } from '../permissions.ts';
import { durationWords, runStateMark, runStateWords, startedWords, workflowWhat } from '../components/workflows/workflowWords.ts';

const TONE = VIEW_TONES['apps-and-routines']!;
type Kind = 'apps' | 'routines' | 'workflows';
type Status = 'listed' | 'pending' | 'rejected' | 'withdrawn';

/** Items drawn at a time; Show more draws this many more. */
const ITEMS_PAGE = 25;

/** One published version, said the same way for an app and a routine. */
interface Version {
  projectId: string;
  projectName: string;
  id: string;
  version: string;
  status: Status;
  name: string;
  purpose: string;
  /** What it reads and does, in a person's words. */
  what: string;
  publishedAt: string;
  publishedBy: string;
  decidedBy?: string;
  reason?: string;
  installs: number;
  /** Apps: what it reads, from outside and sends out (the approved statement when there is one), and the organisation's consent. */
  statement?: AppStatement;
  consent?: { by: string; at: string; revokedAt?: string; revokedBy?: string };
}

/** Every version of one app or routine in one project, newest first. */
interface Item {
  key: string;
  newest: Version;
  versions: Version[];
}

const NOUN: Record<Kind, { one: string; many: string; title: string }> = {
  apps: { one: 'app', many: 'apps', title: 'Apps' },
  routines: { one: 'routine', many: 'routines', title: 'Routines' },
  workflows: { one: 'workflow', many: 'workflows', title: 'Workflows' },
};
const KIND_ICON: Record<Kind, ReactNode> = { apps: <LayoutCellsLarge />, routines: <Clock />, workflows: <BranchesRight /> };

const STATUS_MARK: Record<Status, 'connected' | 'medium' | 'disabled' | 'offline'> = { listed: 'connected', pending: 'medium', rejected: 'disabled', withdrawn: 'offline' };

const newer = (a: string, b: string) => {
  const x = a.split('.').map(Number);
  const y = b.split('.').map(Number);
  for (let i = 0; i < 3; i++) if ((x[i] ?? 0) !== (y[i] ?? 0)) return (y[i] ?? 0) - (x[i] ?? 0);
  return 0;
};

function appVersion(l: OrganisationApp): Version {
  const m = l.manifest;
  // The statement the reviewer approved, else what the manifest itself says (Internal unless the app says higher).
  const statement = l.consent?.statement ?? deriveStatement(m);
  const consent = l.consent ? { by: l.consent.by.name, at: l.consent.at, ...(l.consent.revokedAt ? { revokedAt: l.consent.revokedAt } : {}), ...(l.consent.revokedBy ? { revokedBy: l.consent.revokedBy.name } : {}) } : undefined;
  return { projectId: l.projectId, projectName: l.projectName, id: l.appId, version: l.version, status: l.status, name: m.name, purpose: m.purpose, what: statementLines(statement).join(' · '), publishedAt: l.publishedAt, publishedBy: l.publishedBy.name, ...(l.decidedBy ? { decidedBy: l.decidedBy.name } : {}), ...(l.reason ? { reason: l.reason } : {}), installs: l.installs, statement, ...(consent ? { consent } : {}) };
}

function routineVersion(l: OrganisationRoutine): Version {
  const m = l.manifest;
  const asks = m.parameters.length ? `; asks whoever adds it ${m.parameters.length === 1 ? 'one question' : `${m.parameters.length} questions`}` : '';
  return { projectId: l.projectId, projectName: l.projectName, id: l.routineId, version: l.version, status: l.status, name: m.name, purpose: m.purpose, what: `${ROUTINE_RUN_WORDS[m.runs]}, ${ROUTINE_SCHEDULE_WORDS[m.scheduleKind].toLowerCase()}${asks}`, publishedAt: l.publishedAt, publishedBy: l.publishedBy.name, ...(l.decidedBy ? { decidedBy: l.decidedBy.name } : {}), ...(l.reason ? { reason: l.reason } : {}), installs: l.installs };
}

function workflowVersion(l: OrganisationWorkflow): Version {
  const m = l.manifest;
  return { projectId: l.projectId, projectName: l.projectName, id: l.workflowId, version: l.version, status: l.status, name: m.name, purpose: m.purpose, what: workflowWhat(m), publishedAt: l.publishedAt, publishedBy: l.publishedBy.name, ...(l.decidedBy ? { decidedBy: l.decidedBy.name } : {}), ...(l.reason ? { reason: l.reason } : {}), installs: l.installs };
}

/** The versions grouped per project and id; an item is shown by its newest listed version, else its newest. */
function itemsOf(versions: Version[]): Item[] {
  const by = new Map<string, Version[]>();
  for (const v of versions) by.set(`${v.projectId}/${v.id}`, [...(by.get(`${v.projectId}/${v.id}`) ?? []), v]);
  return [...by.entries()]
    .map(([key, vs]) => {
      const sorted = vs.sort((a, b) => newer(a.version, b.version));
      return { key, versions: sorted, newest: sorted.find((v) => v.status === 'listed') ?? sorted[0]! };
    })
    .sort((a, b) => a.newest.projectName.localeCompare(b.newest.projectName) || a.newest.name.localeCompare(b.newest.name));
}

const people = (n: number) => (n === 0 ? 'Not added yet' : n === 1 ? '1 person added it' : `${n.toLocaleString()} people added it`);

function publishingWords(kind: Kind, rule: PublishingRule | undefined, allowCommands?: boolean): string {
  const n = NOUN[kind];
  const pub = rule === 'off' ? `People cannot publish ${n.many} at the moment.` : rule === 'review' ? `A published ${n.one} waits for one of the project's owners to approve it.` : `A published ${n.one} is listed at once.`;
  const commands = kind === 'routines' ? (allowCommands ? ' Routines that run a tool on the machine may be shared.' : ' Only routines that ask an agent or file work may be shared.') : kind === 'workflows' ? (allowCommands ? ' Workflows that run a script from the repository may be shared.' : ' Only workflows without a script from the repository may be shared.') : '';
  return `${pub}${commands} The organisation's policy decides this, under Policies.`;
}

export function AppsAndRoutinesPage() {
  // `?show=routines` opens on routines (a link from a routine's review record, for one).
  const [kind, setKind] = useState<Kind>(() => {
    const show = new URLSearchParams(window.location.search).get('show');
    return show === 'routines' || show === 'workflows' ? show : 'apps';
  });
  // D378: the services that start routines when something happens, beside the two catalogues (`?show=events`).
  const [events, setEvents] = useState(() => new URLSearchParams(window.location.search).get('show') === 'events');
  const apps = useLoad(() => sharedCatalogueRoutes.apps(), [], 60_000);
  const routines = useLoad(() => sharedCatalogueRoutes.routines(), [], 60_000);
  const workflows = useLoad(() => sharedCatalogueRoutes.workflows(), [], 60_000);
  const [withdrawing, setWithdrawing] = useState<Item>();
  const [revoking, setRevoking] = useState<Item>();
  // `?open=<id>` opens that app's or routine's details (a link from its review record or its history).
  const [focus] = useState(() => new URLSearchParams(window.location.search).get('open') ?? undefined);
  const [error, setError] = useState<string>();
  // With roles on, Approve and Reject show where the person holds Act on this kind: across the organisation, in the
  // version's project, or in a team (a Team Administrator decides for the projects their team is granted; the API checks
  // that grant). Roles off, as before: the API refuses whoever may not.
  const perms = usePermissions().data;
  const area = kind === 'apps' ? 'workflows.apps' : kind === 'routines' ? 'workflows.routines' : 'workflows.workflows';
  const mayDecide = (v: Version): boolean => {
    if (perms?.mode !== 'roles' || holds(perms, area, 'act')) return true;
    return scopesHolding(perms, area, 'act').some((s) => s.type === 'team' || s.id === v.projectId);
  };

  const versions = kind === 'apps' ? (apps.data?.apps ?? []).map(appVersion) : kind === 'routines' ? (routines.data?.routines ?? []).map(routineVersion) : (workflows.data?.workflows ?? []).map(workflowVersion);
  const all = itemsOf(versions);
  // What only waits for review is in the queue above, not again in the list.
  const everyItem = all.filter((i) => i.versions.some((v) => v.status !== 'pending'));
  // Each item shows its newest version, older ones behind "All N versions"; the list is searched (name, purpose, project, author) and drawn a page at a time.
  const search = useListSearch(150);
  const [drawn, setDrawn] = useState(ITEMS_PAGE);
  const words = search.typed.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const matching = words.length ? everyItem.filter((i) => words.every((w) => [i.newest.name, i.newest.purpose, i.newest.projectName, i.newest.id, i.newest.publishedBy].some((t) => t?.toLowerCase().includes(w)))) : everyItem;
  const items = matching.slice(0, drawn);
  const pending = versions.filter((v) => v.status === 'pending').sort((a, b) => a.publishedAt.localeCompare(b.publishedAt));
  const listed = all.filter((i) => i.newest.status === 'listed');
  const added = versions.reduce((n, v) => n + v.installs, 0);
  const loaded = kind === 'apps' ? apps.data : kind === 'routines' ? routines.data : workflows.data;
  const n = NOUN[kind];
  const icon = KIND_ICON[kind];
  const reload = () => (kind === 'apps' ? apps.reload() : kind === 'routines' ? routines.reload() : workflows.reload());

  return (
    <PageColumn width="data">
      <PageHeader
        tone={TONE}
        icon={<LayoutCellsLarge />}
        title="Apps and routines"
        subtitle="What people share from their workbench for a project: each version, who added it, what waits for an owner's review, how workflows ran, and what starts when something happens."
        actions={
          <Segment aria-label="Show" size="sm" selectedKey={events ? 'events' : kind} onSelectionChange={(k) => (setEvents(k === 'events'), k !== 'events' && setKind(k === 'routines' || k === 'workflows' ? k : 'apps'))} className="w-fit max-w-full">
            <Segment.Item id="apps">Apps</Segment.Item>
            <Segment.Item id="routines">Routines</Segment.Item>
            <Segment.Item id="workflows">Workflows</Segment.Item>
            <Segment.Item id="events">When something happens</Segment.Item>
          </Segment>
        }
      />
      <Explainer id="apps-and-routines" tone={TONE} icon={<LayoutCellsLarge />} title="How apps and routines work" summary="Shared by people for one project, reviewed by its owners, versioned and never changed once published">
        <p>An app is a small screen someone made on their workbench, such as a release board or a spend tracker; a routine is work an agent does on a schedule or when asked. People publish either to a project, and everyone in that project can add it to their own workbench.</p>
        <p>Each version is kept as it was published. Depending on the organisation’s policy, a new one is listed at once or waits for one of the project’s owners to approve it. Approving an app also approves what it reads and what it sends outside the workbench.</p>
        <p>A workflow is work that runs the same way every time: steps a named specialist or team member works on, steps proven code does, and steps a person approves, joined by branches. It runs on a workbench or a node, never here; the organisation lists it, starts it with Run now, puts its approvals in the Inbox and on the phone, and keeps a record of every run.</p>
        <p>When something happens connects a service such as GitHub or Sentry, so a shared routine or workflow starts on the machines that have it when a pipeline fails or an alert fires.</p>
      </Explainer>
      {events ? (
        <PartBoundary key="events" name="When something happens" tone={TONE}>
          <WhenSomethingHappens tone={TONE} />
        </PartBoundary>
      ) : (
        <PartBoundary key={kind} name={n.title} tone={TONE}>
          <ErrorNote error={(kind === 'apps' ? apps.error : kind === 'routines' ? routines.error : workflows.error) ?? error} />
          <div className="art-stats">
            <Stat tone={TONE} icon={icon} label="Listed" value={loaded ? listed.length : undefined} detail={loaded ? `In ${new Set(listed.map((i) => i.newest.projectId)).size} ${new Set(listed.map((i) => i.newest.projectId)).size === 1 ? 'project' : 'projects'}` : ''} />
            <Stat tone={TONE} icon={<CircleQuestion />} label="Waiting for review" value={loaded ? pending.length : undefined} detail={pending.length ? `Oldest ${timeAgo(pending[0]!.publishedAt)}` : 'Nothing waiting'} needsYou={pending.length > 0} />
            {kind === 'workflows' ? <WorkflowRunStats /> : <Stat tone={TONE} icon={<Persons />} label="Added" value={loaded ? added : undefined} detail="By people, across every version" />}
          </div>

          {pending.length > 0 && <Queue kind={kind} items={pending} onChange={reload} mayDecide={mayDecide} />}

          <ListGroup
            title={n.title}
            count={loaded ? listCount(items.length, { one: n.one, many: n.many }, { total: words.length ? undefined : everyItem.length, hasMore: matching.length > items.length, searched: words.length > 0 }) : undefined}
            actions={loaded && (everyItem.length > 6 || words.length) ? <SearchBox label={`Search ${n.many}`} value={search.typed} onChange={(v) => (search.setTyped(v), setDrawn(ITEMS_PAGE))} placeholder={`Search ${n.many}`} className="w-40 max-w-full @lg:w-56" /> : undefined}
            description={publishingWords(kind, loaded?.publishing, kind === 'routines' ? routines.data?.allowCommands : kind === 'workflows' ? workflows.data?.allowScripts : undefined)}
            empty={loaded && items.length === 0 && words.length ? <NoMatch tone={TONE} icon={icon} q={search.typed.trim()} noun={n.many} onClear={search.clear} /> : loaded && items.length === 0 ? <Empty tone={TONE} icon={icon} title={`No ${n.many} shared yet`} body={`When someone publishes ${kind === 'apps' ? 'an app' : kind === 'routines' ? 'a routine' : 'a workflow'} from their workbench to a project, it is listed here with its versions and who added it.`} /> : undefined}
          >
            {!loaded ? <ListBlock className="text-muted">Reading what people shared…</ListBlock> : null}
            {items.map((i) =>
              kind === 'workflows' ? (
                <WorkflowRow key={i.key} item={i} onWithdraw={() => setWithdrawing(i)} />
              ) : (
                <Row key={i.key} kind={kind} item={i} opened={i.newest.id === focus} onWithdraw={() => setWithdrawing(i)} onRevoke={() => setRevoking(i)} />
              ),
            )}
            {matching.length > items.length ? <ShowMore onPress={() => setDrawn((d) => d + ITEMS_PAGE)} /> : null}
          </ListGroup>
          {kind === 'workflows' ? <WorkflowRuns /> : null}
        </PartBoundary>
      )}

      <ConfirmDialog
        isOpen={Boolean(withdrawing)}
        title={`Withdraw ${withdrawing?.newest.name ?? ''}?`}
        confirmLabel="Withdraw"
        danger
        onCancel={() => setWithdrawing(undefined)}
        onConfirm={async () => {
          const i = withdrawing!;
          setWithdrawing(undefined);
          setError(undefined);
          try {
            await (kind === 'apps' ? sharedCatalogueRoutes.withdrawApp(i.newest.projectId, i.newest.id) : kind === 'routines' ? sharedCatalogueRoutes.withdrawRoutine(i.newest.projectId, i.newest.id) : sharedCatalogueRoutes.withdrawWorkflow(i.newest.projectId, i.newest.id));
            reload();
          } catch (e) {
            setError(problemText(e));
          }
        }}
      >
        Every version stops being offered in {withdrawing?.newest.projectName}. {kind === 'apps' ? 'People who already added it keep it on their Home.' : 'Copies people already added keep running where they are, and runs already started finish.'}
      </ConfirmDialog>

      <ConfirmDialog
        isOpen={Boolean(revoking)}
        title={`Withdraw the approval of ${revoking?.newest.name ?? ''}?`}
        confirmLabel="Withdraw approval"
        danger
        onCancel={() => setRevoking(undefined)}
        onConfirm={async () => {
          const i = revoking!;
          setRevoking(undefined);
          setError(undefined);
          try {
            const out = await sharedCatalogueRoutes.revokeApp(i.newest.projectId, i.newest.id);
            if (!out.policy) setError('The approval is withdrawn here. No policy is published for this project yet, so workbenches learn it when one is.');
            apps.reload();
          } catch (e) {
            setError(problemText(e));
          }
        }}
      >
        The organisation stops vouching for what {revoking?.newest.name} reads and sends. Workbenches stop it reading from or sending outside the Workbench at their next policy pull, and each person who has it is told once. People can still use what it shows from inside the Workbench.
      </ConfirmDialog>
    </PageColumn>
  );
}

type OrganisationWorkflow = OrganisationWorkflows['workflows'][number];

/** One workflow: its newest version in a line; pressing it opens the workflow's page, the menu withdraws it. */
function WorkflowRow({ item, onWithdraw }: { item: Item; onWithdraw: () => void }) {
  const v = item.newest;
  const total = item.versions.reduce((n, x) => n + x.installs, 0);
  return (
    <ListRow
      icon={<BranchesRight />}
      title={v.name}
      to={`/apps-and-routines/workflows/${encodeURIComponent(v.projectId)}/${encodeURIComponent(v.id)}`}
      description={
        <>
          <span className="block">{v.purpose}</span>
          <span className="block">{v.what}</span>
          <span className="block tabular-nums">{[`Version ${v.version}`, v.projectName, `by ${v.publishedBy}`, people(total)].join(' · ')}</span>
        </>
      }
      status={<StatusMark state={STATUS_MARK[v.status]} label={ROUTINE_LISTING_STATUS_WORDS[v.status]} />}
      compactControl
      control={v.status !== 'withdrawn' ? <RowMenu label={`More for ${v.name}`} items={[{ id: 'withdraw', label: 'Withdraw…', danger: true }]} onAction={() => onWithdraw()} /> : undefined}
    />
  );
}

/** The fourth figure on the Workflows tab: the runs of the last 30 days, and how many wait for a person. */
function WorkflowRunStats() {
  const runs = useLoad(() => sharedCatalogueRoutes.workflowRuns({ days: 30 }), [], 60_000);
  const list = runs.data?.runs ?? [];
  const waiting = list.filter((r) => r.waitingFor.length && r.run.state === 'running').length;
  const failed = list.filter((r) => r.run.state === 'failed').length;
  return <Stat tone={TONE} icon={<BranchesRight />} label="Runs, last 30 days" value={runs.data ? list.length : undefined} detail={runs.data ? (waiting ? `${waiting} waiting for a person` : failed ? `${failed} failed` : 'None waiting for a person') : ''} needsYou={waiting > 0} />;
}

/** Every workflow's runs of the last 30 days: those waiting for a person first, then the newest. */
function WorkflowRuns() {
  const runs = useLoad(() => sharedCatalogueRoutes.workflowRuns({ days: 30 }), [], 30_000);
  const list = [...(runs.data?.runs ?? [])].sort((a, b) => Number(b.waitingFor.length > 0 && b.run.state === 'running') - Number(a.waitingFor.length > 0 && a.run.state === 'running') || b.run.createdAt.localeCompare(a.run.createdAt));
  return (
    <ListGroup
      title="Runs"
      count={runs.data ? list.length : undefined}
      description="The last 30 days, as the machines that ran them reported. A run waiting for a person is at the top."
      empty={runs.data && list.length === 0 ? <Empty tone={TONE} icon={<BranchesRight />} title="No runs yet" body="When a workflow runs on a workbench or a node of one of your projects, or someone starts one here, its run is listed with each step." /> : undefined}
    >
      <ErrorNote error={runs.error} />
      {!runs.data && !runs.error ? <ListBlock className="text-muted">Reading the runs…</ListBlock> : null}
      {list.slice(0, 50).map((r) => (
        <ListRow
          key={`${r.projectId}/${r.run.id}`}
          icon={<BranchesRight />}
          title={r.workflowName}
          to={`/apps-and-routines/workflow-runs/${encodeURIComponent(r.projectId)}/${encodeURIComponent(r.run.id)}`}
          description={
            <>
              <span className="block">{[startedWords(r.run), r.deviceName ? `on ${r.deviceName}` : '', r.projectName].filter(Boolean).join(' · ')}</span>
              <span className="block tabular-nums">{[timeAgo(r.run.createdAt), durationWords(r.durationMs, r.run.state)].filter(Boolean).join(' · ')}</span>
            </>
          }
          status={<StatusMark state={runStateMark(r)} label={runStateWords(r)} />}
        />
      ))}
    </ListGroup>
  );
}

/** An app's label when it is above Internal, and "External data" when it reads from outside the Workbench: a dot and words, never colour alone. */
function StatementMarks({ statement }: { statement: AppStatement | undefined }) {
  if (!statement) return null;
  const high = labelRank(statement.label) > labelRank('internal');
  const outside = externalNames(statement);
  if (!high && !outside.length) return null;
  return (
    <span className="mt-0.5 flex flex-wrap gap-x-4 gap-y-0.5">
      {high ? <StatusMark state={statement.label === 'restricted' ? 'high' : 'medium'} label={labelWord(statement.label)} /> : null}
      {outside.length > 0 ? <StatusMark state="online" label={`External data: ${outside.join(', ')}`} /> : null}
    </span>
  );
}

/** Who approved what an app reads and sends, or who withdrew that approval. */
const consentWords = (c: NonNullable<Version['consent']>) => (c.revokedAt ? `Approval withdrawn ${timeAgo(c.revokedAt)}${c.revokedBy ? ` by ${c.revokedBy}` : ''}` : `Approved for everyone who adds it by ${c.by}, ${timeAgo(c.at)}`);

/** One app or routine: its newest version in a line, and every version (and, for an app, its history) on request. */
function Row({ kind, item, onWithdraw, onRevoke, opened }: { kind: Kind; item: Item; onWithdraw: () => void; onRevoke: () => void; opened?: boolean }) {
  const [open, setOpen] = useState(Boolean(opened));
  const v = item.newest;
  const total = item.versions.reduce((n, x) => n + x.installs, 0);
  const revocable = kind === 'apps' && item.versions.some((x) => x.consent && !x.consent.revokedAt);
  return (
    <ListRow
      icon={kind === 'apps' ? <LayoutCellsLarge /> : <Clock />}
      title={v.name}
      description={
        <>
          <span className="block">{v.purpose}</span>
          {kind === 'apps' && v.what ? <span className="block">{v.what}</span> : null}
          <span className="block tabular-nums">{[`Version ${v.version}`, v.projectName, `by ${v.publishedBy}`, people(total)].join(' · ')}</span>
          <StatementMarks statement={v.statement} />
        </>
      }
      status={<StatusMark state={STATUS_MARK[v.status]} label={ROUTINE_LISTING_STATUS_WORDS[v.status]} />}
      compactControl
      control={
        <>
          <RowMenu
            label={`More for ${v.name}`}
            items={[
              { id: 'details', label: open ? 'Hide the versions' : kind === 'apps' ? 'Versions and history' : item.versions.length === 1 ? 'The version' : `All ${item.versions.length} versions` },
              revocable && { id: 'revoke', label: 'Withdraw the approval…' },
              v.status !== 'withdrawn' && { id: 'withdraw', label: 'Withdraw…', danger: true },
            ]}
            onAction={(id) => (id === 'details' ? setOpen(!open) : id === 'revoke' ? onRevoke() : onWithdraw())}
          />
        </>
      }
    >
      {open && (
        <div className="grid gap-3 rounded-lg bg-surface-secondary px-3 py-2 text-sm">
          <ul className="grid gap-1.5">
            {item.versions.map((x) => (
              <li key={x.version} className="flex items-baseline gap-3">
                <div className="min-w-0 flex-1">
                  <span className="font-medium tabular-nums">{x.version}</span>
                  {/* An app's statement is said once above; a version shows it only where it differs. */}
                  {kind === 'routines' || x.what !== v.what ? <span className="text-muted"> · {x.what}</span> : null}
                  <div className="text-xs text-muted">
                    {[`Published ${timeAgo(x.publishedAt)} by ${x.publishedBy}`, x.decidedBy ? `${x.status === 'rejected' ? 'not accepted' : 'decided'} by ${x.decidedBy}` : '', x.reason ? `“${x.reason}”` : '', x.consent ? consentWords(x.consent) : '', people(x.installs)].filter(Boolean).join(' · ')}
                  </div>
                </div>
                <span className="shrink-0 text-xs text-muted">{ROUTINE_LISTING_STATUS_WORDS[x.status]}</span>
              </li>
            ))}
          </ul>
          {kind === 'apps' && <History projectId={v.projectId} appId={v.id} />}
          {revocable && <p className="text-xs text-muted">Withdrawing the approval (from the menu) stops this app reading from or sending outside the Workbench on every machine at its next policy pull. The app itself stays.</p>}
        </div>
      )}
    </ListRow>
  );
}

/** What the workbenches recorded about one app: consents given and withdrawn, what the policy stopped, and data sent out. */
function History({ projectId, appId }: { projectId: string; appId: string }) {
  const rows = useLoad(() => sharedCatalogueRoutes.appAudit(projectId, appId), [projectId, appId]);
  const list = rows.data?.entries ?? [];
  return (
    <div className="grid gap-1">
      <div className="text-xs font-medium text-muted">History</div>
      <ErrorNote error={rows.error} />
      {rows.data && !list.length ? <p className="text-xs text-muted">No machine has reported a consent, a refusal or data sent out for this app yet.</p> : null}
      <ul className="grid gap-0.5">
        {list.slice(0, 20).map((e, i) => (
          <li key={`${e.at}-${i}`} className="text-xs">
            <span>{auditSentence(e) ?? e.action}</span>
            <span className="text-muted"> · {[e.who, timeAgo(e.at)].filter(Boolean).join(' · ')}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Versions waiting for an owner: Approve, or Reject with the reason the author reads. */
function Queue({ kind, items, onChange, mayDecide }: { kind: Kind; items: Version[]; onChange: () => void; mayDecide: (v: Version) => boolean }) {
  const [rejecting, setRejecting] = useState<string>();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState<string>();
  const [error, setError] = useState<string>();
  const decide = async (v: Version, decision: 'approve' | 'reject') => {
    const key = `${v.projectId}/${v.id}@${v.version}`;
    setBusy(key);
    setError(undefined);
    try {
      // An approval is the organisation's consent to the statement shown here (D364).
      const body = { decision, ...(decision === 'reject' ? { reason: reason.trim() } : {}), ...(decision === 'approve' && v.statement ? { statement: v.statement } : {}) };
      await (kind === 'apps' ? sharedCatalogueRoutes.decideApp(v.projectId, v.id, v.version, body) : kind === 'routines' ? sharedCatalogueRoutes.reviewRoutine(v.projectId, v.id, v.version, body) : sharedCatalogueRoutes.reviewWorkflow(v.projectId, v.id, v.version, { decision, ...(decision === 'reject' ? { reason: reason.trim() } : {}) }));
      setRejecting(undefined);
      setReason('');
      onChange();
    } catch (e) {
      setError(problemText(e));
    } finally {
      setBusy(undefined);
    }
  };
  const icon: ReactNode = KIND_ICON[kind];
  return (
    <ListGroup title="Waiting for review" count={items.length} description="Oldest first. The person who published one cannot approve it.">
      {items.map((v) => {
        const key = `${v.projectId}/${v.id}@${v.version}`;
        return (
          <ListRow
            key={key}
            icon={icon}
            title={`${v.publishedBy} wants to share ${v.name} ${v.version} in ${v.projectName}`}
            description={
              <>
                <span className="block">{v.purpose}</span>
                <span className="block">{[v.what, timeAgo(v.publishedAt)].join(' · ')}</span>
                <StatementMarks statement={v.statement} />
              </>
            }
            control={
              rejecting !== key && mayDecide(v) ? (
                <>
                  <Button size="sm" isPending={busy === key} isDisabled={Boolean(busy) && busy !== key} onPress={() => void decide(v, 'approve')}>
                    Approve
                  </Button>
                  <Button size="sm" variant="ghost" isDisabled={Boolean(busy)} onPress={() => setRejecting(key)}>
                    Reject
                  </Button>
                </>
              ) : undefined
            }
          >
            {rejecting === key && (
              <div className="grid gap-2 @[35rem]:flex @[35rem]:items-end">
                <label className="grid flex-1 gap-1 text-xs text-muted">
                  Why it is not accepted (the person who published it reads this)
                  <input className={inputClass} value={reason} maxLength={400} onChange={(ev) => setReason(ev.target.value)} autoFocus />
                </label>
                <div className="flex gap-2">
                  <Button size="sm" variant="ghost" onPress={() => setRejecting(undefined)}>
                    Cancel
                  </Button>
                  <Button size="sm" variant="danger" isDisabled={!reason.trim()} isPending={busy === key} onPress={() => void decide(v, 'reject')}>
                    Reject
                  </Button>
                </div>
              </div>
            )}
          </ListRow>
        );
      })}
      {error ? (
        <ListBlock>
          <ErrorNote error={error} />
        </ListBlock>
      ) : null}
    </ListGroup>
  );
}
